import { FunctionMap, StoryState } from '../../types';
import { RemoveBlockedUserStories, SetRecommendedStories, HandleNewStories, ApproveEntry, FSAInterface } from '../actions';
import {
  ADD_STORY_TO_FOLDER,
  REMOVE_STORY_FROM_FOLDER,
  MARK_STORY_READ,
  DELETE_STORY,
  MOVE_STORY,
  REMOVE_BLOCKED_USER_STORIES,
  GET_STORIES_SUCCESS,
  CLEAR_STORIES,
  GET_RECOMMENDED_STORIES_SUCCESS,
  DISMISS_RECOMMENDED_STORIES,
  CREATE_POST_SUCCESS,
  PIN_STORY,
  HANDLE_NEW_STORIES,
  CLEAR_STORY_PUSH_INFO,
  APPROVE_COMPETITION_ENTRY_REQUEST,
  APPROVE_COMPETITION_ENTRY_SUCCESS,
  REJECT_COMPETITION_ENTRY_REQUEST,
  FETCH_PROPOSED_ENTRY_SUCCESS,
  UNVERIFY_COMPETITION_ENTRY_REQUEST,
  UNVERIFY_COMPETITION_ENTRY_SUCCESS,
  UPDATE_POST_SUCCESS,
  DELETE_ENTRIES_REQUEST,
  REMOVE_FOLDER_FROM_STORY_SUCCESS,
  ADD_FOLDER_TO_STORY_SUCCESS,
  FORCE_ADVANCE_ENTRY_REQUEST,
  FORCE_ADVANCE_ENTRY_SUCCESS,
  FORCE_REMOVE_ENTRY_STAGE_SUCCESS,
  UPDATE_STORY_SUCCESS,
  REMOVE_ENTRY_MEDIA_SUCCESS,
  REMOVE_ENTRY_MEDIA_REQUEST,
  UPDATE_ENTRY_MEDIA_REQUEST,
  UPDATE_ENTRY_MEDIA_SUCCESS,
  CREATE_ENTRY_SUCCESS,
  CLEAR_ENTRY_PROCESSING,
  REMOVE_ENTRY_MEDIA_THUMBNAIL_REQUEST,
  REMOVE_ENTRY_MEDIA_THUMBNAIL_SUCCESS,
  UPDATE_ENTRY_MEDIA_THUMBNAIL_REQUEST,
  UPDATE_ENTRY_MEDIA_THUMBNAIL_SUCCESS,
} from '../constants';
import { createReducer } from './Utils';
import { postsShownValues } from '../../constants';
import Story, { StoryMedia } from '../../models/Story';
import { compose, get, remove, set, unset } from 'lodash/fp';
import { UnknownAction } from 'redux';

export const storiesDefaultState: StoryState = {
  stories: [] as Story[],
  storiesByKey: {} as { [storyId: string]: Story },
  total: 0,
  recommendedStories: [] as Story[],
  recommendedStoriesByKey: {} as { [storyId: string]: Story },
  pushInfo: { when: 0, count: 0, updates: 0 },
};

function replaceInStories(newStory: Story, stories: Story[]) {
  const index = stories.findIndex((s) => s.id === newStory.id);
  if (index !== -1) {
    return [...stories.slice(0, index), newStory, ...stories.slice(index + 1)];
  }

  return stories;
}

function removeFromStories(removeStory: Story, stories: Story[]) {
  const index = stories.findIndex((s) => s.id === removeStory.id);
  if (index !== -1) {
    return [...stories.slice(0, index), ...stories.slice(index + 1)];
  }

  return stories;
}

// Filter out posts from specific user
function userFilter(story: Story, action: RemoveBlockedUserStories) {
  return !(story.username === action.username && story.type === action.socialService);
}

function clearStories(state: StoryState) {
  return {
    ...state,
    total: 0,
    loading: false,
    storiesByKey: {},
    stories: [],
    recommendedStoriesByKey: {},
    recommendedStories: [],
    pushInfo: { when: 0, count: 0, updates: 0 },
  };
}

export function setStories(state: StoryState, action: any) {
  const storiesByKey = action.additive ? { ...state.storiesByKey } : {};
  const dedupedStories = action.payload.filter((story: Story) => {
    storiesByKey[story.id] = story;
    return action.additive ? !state.storiesByKey[story.id] : true;
  });

  return {
    ...state,
    storiesByKey,
    total: action.total,
    loading: false,
    stories: action.additive ? [...state.stories, ...dedupedStories] : dedupedStories,
  };
}

function addStoryToFolder(state: StoryState, action: any) {
  const story = state.storiesByKey[action.storyId];
  const folders = { ...story.folders, [action.folderId]: true };
  const withFolder = { ...story, folders };
  const storiesByKey = { ...state.storiesByKey, [withFolder.id]: withFolder };

  const stories = replaceInStories(withFolder, state.stories);

  const newState = { ...state, storiesByKey, stories };

  return newState;
}

function removeStoryFromFolder(state: StoryState, action: any) {
  const story = state.storiesByKey[action.storyId];
  const { [action.folderId]: omitted, ...folders } = story.folders;
  const withoutFolder = { ...story, folders };
  const storiesByKey = { ...state.storiesByKey, [withoutFolder.id]: withoutFolder };
  const stories = removeFromStories(withoutFolder, state.stories);

  return { ...state, storiesByKey, stories };
}

function markStoryRead(state: StoryState, action: any) {
  const withRead = { ...state.storiesByKey[action.storyId], read: true };
  const storiesByKey = { ...state.storiesByKey, [withRead.id]: withRead };
  const stories = replaceInStories(withRead, state.stories);

  return { ...state, storiesByKey, stories };
}

function deleteStory(state: StoryState, action: any) {
  let storiesByKey = state.storiesByKey;
  let stories = state.stories;
  let recommendedStoriesByKey = state.recommendedStoriesByKey;
  let recommendedStories = state.recommendedStories;
  action.storyIds.forEach((storyId: string) => {
    if (storyId in state.storiesByKey) {
      const { [storyId]: omitted, ...newStoriesByKey } = storiesByKey;
      storiesByKey = newStoriesByKey;
      stories = removeFromStories(omitted, stories);
    }

    if (storyId in state.recommendedStoriesByKey) {
      const { [storyId]: omitted, ...newRecommendedStoriesByKey } = recommendedStoriesByKey;
      recommendedStoriesByKey = newRecommendedStoriesByKey;
      recommendedStories = removeFromStories(omitted, recommendedStories);
    }
  });
  return { ...state, stories, storiesByKey, recommendedStoriesByKey, recommendedStories };
}

function moveStory(state: StoryState, action: any) {
  const story = state.storiesByKey[action.storyId];
  let { stories } = state;

  if (story) {
    const index = state.stories.findIndex((s) => s.id === action.storyId);
    if (index !== -1) {
      if (index < action.position) {
        stories = [
          ...stories.slice(0, index),
          ...stories.slice(index + 1, action.position + 1),
          story,
          ...stories.slice(action.position + 1),
        ];
      } else {
        stories = [
          ...stories.slice(0, action.position),
          story,
          ...stories.slice(action.position, index),
          ...stories.slice(index + 1),
        ];
      }
    }

    return { ...state, stories };
  }

  return state;
}

function removeBlockedUserStories(state: StoryState, action: any) {
  const filteredStories = state.stories.filter((story) => userFilter(story, action));
  const filteredRecommendedStories = state.recommendedStories.filter((story) => userFilter(story, action));
  const storiesByKey = {};
  filteredStories.forEach((story) => (storiesByKey[story.id] = story));

  const recommendedStoriesByKey = {};
  filteredRecommendedStories.forEach((story) => (recommendedStoriesByKey[story.id] = story));
  return {
    ...state,
    storiesByKey,
    recommendedStoriesByKey,
    stories: filteredStories,
    recommendedStories: filteredRecommendedStories,
  };
}

function setRecommendedStories(state: StoryState, action: FSAInterface<SetRecommendedStories['payload']>) {
  const recommendedStories = get('payload.hits', action);
  const recommendedStoriesByKey = { ...state.recommendedStoriesByKey };

  recommendedStories.forEach((story: Story) => (recommendedStoriesByKey[story.id] = story));

  return {
    ...state,
    recommendedStoriesByKey,
    recommendedStories: [...state.recommendedStories, ...recommendedStories],
  };
}

function dismissRecommendedStories(state: StoryState, action: UnknownAction) {
  const { recommendedStories, recommendedStoriesByKey } = state;
  const updatedStoriesByKey = { ...recommendedStoriesByKey };
  const dismissed = action.dismissed as string[];

  const updatedStories = recommendedStories.filter((story) => dismissed.indexOf(story.id) === -1);

  dismissed.forEach((storyId) => {
    delete updatedStoriesByKey[storyId];
  });

  return {
    ...state,
    recommendedStories: updatedStories,
    recommendedStoriesByKey: updatedStoriesByKey,
  };
}

function createStory(state: StoryState, action: any) {
  const { stories: curStories, storiesByKey } = state;
  const { payload } = action;
  const newStory = {
    id: payload.postId,
    type: payload.type,
    folders: payload.folders,
    ...payload.data,
    customLink: payload.customLink,
  };
  const newStories = [newStory, ...curStories];
  const newStoriesByKey = { [newStory.id]: newStory, ...storiesByKey };
  return { ...state, stories: newStories, storiesByKey: newStoriesByKey };
}

function pinStory(state: StoryState, action: UnknownAction) {
  const { storyId } = action;
  const newStories = [...state.stories].filter((story: Story) => story.id !== storyId);
  const newStoriesByKey = { ...state.storiesByKey };
  delete newStoriesByKey[storyId as string];
  return { ...state, stories: newStories, storiesByKey: newStoriesByKey };
}

function handleNewStories(state: StoryState, action: FSAInterface<HandleNewStories['payload']>) {
  const { data } = action.payload || {};
  const newData = data.inboxUpdate;

  if (!state.pushInfo.when && !state.pushInfo.updates) {
    return { ...state, pushInfo: { ...state.pushInfo, updates: 1, when: newData.when } };
  }

  if (state.pushInfo.when < newData.when && newData.count) {
    return {
      ...state,
      pushInfo: {
        when: newData.when,
        count: state.pushInfo.count + newData.count,
        updates: state.pushInfo.updates + 1,
      },
    };
  }

  return state;
}

function clearStoryPushInfo(state: StoryState) {
  return { ...state, pushInfo: { when: 0, count: 0, updates: state.pushInfo.updates } };
}

function approveEntryOptimistic(state: StoryState, action: FSAInterface<ApproveEntry['payload']>) {
  const entryIds = get('payload.postIds', action);
  const stageNum = get('payload.stageNum', action);
  const accepted = get('payload.accepted', action);
  const moveEntry = get('meta.moveEntry', action);
  const { stories, storiesByKey } = state;
  let newStoriesByKey = {};
  let newStories = [] as Story[];

  const stageNumToWrite = moveEntry ? stageNum : stageNum + 1;

  entryIds.forEach((entryId: string) => {
    const storyIndex = stories.findIndex((story) => story.id === entryId);
    newStoriesByKey = set([entryId, 'stages', `stage${stageNumToWrite}`, 'accepted'], accepted, storiesByKey);
    newStories = set([storyIndex, 'stages', `stage${stageNumToWrite}`, 'accepted'], accepted, stories);

    if (moveEntry && !accepted) {
      const storyToMove = newStories.splice(storyIndex, 1);
      newStories.push(storyToMove[0]);
    }
  });

  return { ...state, storiesByKey: newStoriesByKey, stories: newStories };
}

function setEntry(state: StoryState, action: any) {
  const { payload } = action;
  const { stories, storiesByKey = {} } = state;
  const newStoriesByKey = { ...storiesByKey };
  let newStories = [] as Story[];

  payload.forEach((story: Story) => {
    const storyIndex = stories.findIndex((s: Story) => s.id === story.id);
    newStoriesByKey[story.id] = story;
    newStories = set([storyIndex], story, stories);
  });

  return { ...state, storiesByKey: newStoriesByKey, stories: newStories };
}

function setNewProposedEntries(state: StoryState, action: any) {
  const { payload } = action;

  const newStoriesByKey = { ...state.storiesByKey };
  const newStories = [...state.stories];

  payload.forEach((story: Story) => {
    newStoriesByKey[story.id] = story;
    const index = state.stories.findIndex((s: Story) => {
      return story.id === s.id;
    });
    newStories.splice(index, 1);
    newStories.unshift(story);
  });

  return { ...state, stories: newStories, storiesByKey: newStoriesByKey };
}

function unVerifyEntryOptimistic(state: StoryState, action: FSAInterface<ApproveEntry['payload']>) {
  const entryIds = get('payload.postIds', action);
  const stageNum = get('payload.stageNum', action);
  const moveEntry = get('meta.moveEntry', action);
  const { stories, storiesByKey } = state;
  let newStoriesByKey = {};
  let newStories = [] as Story[];

  const stageNumToWrite = moveEntry ? stageNum : stageNum + 1;

  entryIds.forEach((entryId: string) => {
    const storyIndex = stories.findIndex((story) => story.id === entryId);
    newStoriesByKey = unset([entryId, 'stages', `stage${stageNumToWrite}`, 'accepted'], storiesByKey);
    newStories = unset([storyIndex, 'stages', `stage${stageNumToWrite}`, 'accepted'], stories);
  });

  return { ...state, storiesByKey: newStoriesByKey, stories: newStories };
}

function updateStory(state: StoryState, action: FSAInterface<any>) {
  const { stories, storiesByKey } = state;
  const updatedStoryId = action?.payload?.id;
  const indexToUpdate = stories.findIndex((story) => story.id === updatedStoryId);
  const storyToUpdate = { ...stories[indexToUpdate] };
  storyToUpdate.caption = action?.payload?.caption;
  storyToUpdate.media = action?.payload?.media;
  storyToUpdate.fullname = action?.payload?.fullname;
  storyToUpdate.time = action?.payload?.time;
  storyToUpdate.type = action?.payload?.type;
  storyToUpdate.isProcessing = action?.payload?.isProcessing;
  storyToUpdate.id = action?.payload?.id;
  const newStories = [...stories];
  newStories.splice(indexToUpdate, 1, storyToUpdate);
  const newStoriesByKey = {
    ...storiesByKey,
  };
  newStoriesByKey[updatedStoryId] = storyToUpdate;
  return { ...state, storiesByKey: newStoriesByKey, stories: newStories };
}

function removeMediaOptimistic(state: StoryState, action: FSAInterface<any>) {
  const { stories, storiesByKey } = state;
  const updatedStoryId = action?.meta?.id;
  const indexToUpdate = stories.findIndex((story) => story.id === updatedStoryId);
  const storyToUpdate = { ...stories[indexToUpdate] };
  storyToUpdate.media = [];
  const newStories = [...stories];
  newStories.splice(indexToUpdate, 1, storyToUpdate);
  const newStoriesByKey = {
    ...storiesByKey,
  };
  newStoriesByKey[updatedStoryId] = storyToUpdate;
  return { ...state, storiesByKey: newStoriesByKey, stories: newStories };
}

function removeVideoThumbnailOptimistic(state: StoryState, action: FSAInterface<any>) {
  const { stories, storiesByKey } = state;
  const updatedStoryId = action?.meta?.id;
  const indexToUpdate = stories.findIndex((story) => story.id === updatedStoryId);
  const storyToUpdate = { ...stories[indexToUpdate] };
  const newStories = [...stories];
  newStories.splice(indexToUpdate, 1, storyToUpdate);
  const newStoriesByKey = {
    ...storiesByKey,
  };
  newStoriesByKey[updatedStoryId] = {
    ...storyToUpdate,
    media: [{ ...storyToUpdate.media[0], thumbnailFallback: undefined } as StoryMedia],
  };
  return { ...state, storiesByKey: newStoriesByKey, stories: newStories };
}

function updateVideoThumbnailOptimistic(state: StoryState, action: FSAInterface<any>) {
  const { stories, storiesByKey } = state;
  const updatedStoryId = action?.meta?.id;
  const indexToUpdate = stories.findIndex((story) => story.id === updatedStoryId);
  const storyToUpdate = { ...stories[indexToUpdate] };
  const newStories = [...stories];
  newStories.splice(indexToUpdate, 1, storyToUpdate);
  const newStoriesByKey = {
    ...storiesByKey,
  };
  newStoriesByKey[updatedStoryId] = storyToUpdate;
  return { ...state, storiesByKey: newStoriesByKey, stories: newStories };
}

function updateMediaOptimistic(state: StoryState, action: FSAInterface<any>) {
  const { stories, storiesByKey } = state;
  const updatedStoryId = action?.meta?.id;
  const indexToUpdate = stories.findIndex((story) => story.id === updatedStoryId);
  const storyToUpdate = { ...stories[indexToUpdate], isProcessing: true, media: [{} as StoryMedia] };
  const newStories = [...stories];
  newStories.splice(indexToUpdate, 1, storyToUpdate);
  const newStoriesByKey = {
    ...storiesByKey,
  };
  newStoriesByKey[updatedStoryId] = storyToUpdate;
  return { ...state, storiesByKey: newStoriesByKey, stories: newStories };
}

function deleteStories(state: StoryState, action: any) {
  let storiesByKey = state.storiesByKey;
  let stories = state.stories;
  action.payload.postIds.forEach((storyId: string) => {
    if (storyId in state.storiesByKey) {
      const { [storyId]: omitted, ...newStoriesByKey } = storiesByKey;
      storiesByKey = newStoriesByKey;
      stories = removeFromStories(omitted, stories);
    }
  });
  return { ...state, stories, storiesByKey };
}

function handleRemoveFolder(state: StoryState, action: any) {
  const { payload, meta } = action;
  const { stories, storiesByKey } = state;
  const updatedStoryId = payload?.postId;
  const indexToUpdate = stories.findIndex((story) => story.id === updatedStoryId);

  if (meta?.filters?.postsShown === postsShownValues.PINNED || meta?.currentBoardId === payload?.folderId) {
    const newStories = remove((story: Story) => story.id === updatedStoryId, stories);
    const newStoriesByKey = unset([updatedStoryId], storiesByKey);

    return { ...state, stories: newStories, storiesByKey: newStoriesByKey };
  }

  const newStories = unset([indexToUpdate, 'folders', payload?.folderId], stories);
  const newStoriesByKey = unset([updatedStoryId, 'folders', payload?.folderId], storiesByKey);

  return { ...state, stories: newStories, storiesByKey: newStoriesByKey };
}

function handleAddFoldersToRecommendedStory(state: StoryState, action: any) {
  const { payload, meta } = action;
  const { recommendedStories, recommendedStoriesByKey } = state;
  const folderToAdd = meta.boardFolders[payload?.folderId];

  const updatedStoryId = payload?.postIds[0];
  const indexToUpdate = recommendedStories.findIndex((story) => story.id === updatedStoryId);

  // functions to run on stories array
  const addNewStories = set([indexToUpdate, 'folders', payload?.folderId], folderToAdd);
  const removeStory = remove((story: Story) => story.id === updatedStoryId);
  const arrayFunctions = [addNewStories];

  let newStoriesByKey = set([updatedStoryId, 'folders', payload?.folderId], folderToAdd, recommendedStoriesByKey);

  if (meta?.filters?.postsShown === postsShownValues.UNPINNED) {
    arrayFunctions.unshift(removeStory);
    delete newStoriesByKey[updatedStoryId];
  }

  const newStories = compose(...arrayFunctions)(recommendedStories);

  return { ...state, recommendedStories: newStories, recommendedStoriesByKey: newStoriesByKey };
}

function handleAddFolderToStory(state: StoryState, action: any) {
  const { payload, meta } = action;
  const { stories, storiesByKey } = state;
  const folderToAdd = meta.boardFolders[payload?.folderId];

  const updatedStoryId = payload?.postIds[0];
  const indexToUpdate = stories.findIndex((story) => story.id === updatedStoryId);

  // functions to run on stories array
  const addNewStories = set([indexToUpdate, 'folders', payload?.folderId], folderToAdd);
  const removeStory = remove((story: Story) => story.id === updatedStoryId);
  const arrayFunctions = [addNewStories];

  let newStoriesByKey = set([updatedStoryId, 'folders', payload?.folderId], folderToAdd, storiesByKey);

  if (meta?.filters?.postsShown === postsShownValues.UNPINNED) {
    arrayFunctions.unshift(removeStory);
    delete newStoriesByKey[updatedStoryId];
  }

  const newStories = compose(...arrayFunctions)(stories);

  const recommendedState = handleAddFoldersToRecommendedStory(state, action);

  return { ...recommendedState, stories: newStories, storiesByKey: newStoriesByKey };
}

function removeEntryStage(state: StoryState, action: FSAInterface<any>) {
  const entryIds = get('meta.postIds', action);
  const { stories, storiesByKey } = state;
  let newStoriesByKey = { ...storiesByKey };
  let newStories = [...stories] as Story[];
  entryIds.forEach((entryId: string) => {
    const storyIndex = stories.findIndex((story) => story.id === entryId);
    delete newStoriesByKey[entryId];
    newStories.splice(storyIndex, 1);
  });
  return { ...state, storiesByKey: newStoriesByKey, stories: newStories };
}

function setStory(state: StoryState, action: FSAInterface<any>) {
  const postIdToSet = action.payload.id;
  const newStories = [...state.stories];
  const newStoriesByKey = { ...state.storiesByKey };
  newStoriesByKey[postIdToSet] = action.payload;
  const storyIndex = newStories.findIndex((story: Story) => story.id === postIdToSet);
  newStories[storyIndex] = action.payload;
  return { ...state, storiesByKey: newStoriesByKey, stories: newStories };
}

function addEntryStory(state: StoryState, action: FSAInterface<any>) {
  const postIdToSet = action.payload.id;
  const newStories = [{ ...action.payload }, ...state.stories];
  const newStoriesByKey = { ...state.storiesByKey };
  newStoriesByKey[postIdToSet] = action.payload;
  return { ...state, storiesByKey: newStoriesByKey, stories: newStories, total: state.total + 1 };
}

function clearStoryProcessing(state: StoryState, action: FSAInterface<any>) {
  const { stories, storiesByKey } = state;
  const updatedStoryId = action?.meta?.entryId;
  const indexToUpdate = stories.findIndex((story) => story.id === updatedStoryId);
  const storyToUpdate = { ...stories[indexToUpdate] };
  storyToUpdate.isProcessing = false;
  const newStories = [...stories];
  newStories.splice(indexToUpdate, 1, storyToUpdate);
  const newStoriesByKey = {
    ...storiesByKey,
  };
  newStoriesByKey[updatedStoryId] = storyToUpdate;
  return { ...state, storiesByKey: newStoriesByKey, stories: newStories };
}

function SetEntryStatus(state: StoryState, action: any) {
  const { payload } = action;
  const { stories, storiesByKey = {} } = state;
  const newStoriesByKey = { ...storiesByKey };
  let newStories = [] as Story[];
  const storyIndex = stories.findIndex((s: Story) => s.id === payload.id);
  newStoriesByKey[payload.id] = { ...storiesByKey[payload.id], isDisqualified: payload.isDisqualified };
  newStories = set([storyIndex], { ...stories[storyIndex], isDisqualified: payload.isDisqualified }, stories);
  return { ...state, storiesByKey: newStoriesByKey, stories: newStories };
}

const functionMap: FunctionMap = {
  [GET_STORIES_SUCCESS]: setStories,
  [ADD_STORY_TO_FOLDER]: addStoryToFolder,
  [REMOVE_STORY_FROM_FOLDER]: removeStoryFromFolder,
  [MOVE_STORY]: moveStory,
  [MARK_STORY_READ]: markStoryRead,
  [DELETE_STORY]: deleteStory,
  [REMOVE_BLOCKED_USER_STORIES]: removeBlockedUserStories,
  [CLEAR_STORIES]: clearStories,
  [GET_RECOMMENDED_STORIES_SUCCESS]: setRecommendedStories,
  [DISMISS_RECOMMENDED_STORIES]: dismissRecommendedStories,
  [CREATE_POST_SUCCESS]: createStory,
  [PIN_STORY]: pinStory,
  [HANDLE_NEW_STORIES]: handleNewStories,
  [CLEAR_STORY_PUSH_INFO]: clearStoryPushInfo,
  [APPROVE_COMPETITION_ENTRY_REQUEST]: approveEntryOptimistic,
  [APPROVE_COMPETITION_ENTRY_SUCCESS]: setEntry,
  [REJECT_COMPETITION_ENTRY_REQUEST]: approveEntryOptimistic,
  [FETCH_PROPOSED_ENTRY_SUCCESS]: setNewProposedEntries,
  [UNVERIFY_COMPETITION_ENTRY_REQUEST]: unVerifyEntryOptimistic,
  [UNVERIFY_COMPETITION_ENTRY_SUCCESS]: setEntry,
  [UPDATE_POST_SUCCESS]: updateStory,
  [DELETE_ENTRIES_REQUEST]: deleteStories,
  [REMOVE_FOLDER_FROM_STORY_SUCCESS]: handleRemoveFolder,
  [ADD_FOLDER_TO_STORY_SUCCESS]: handleAddFolderToStory,
  [FORCE_ADVANCE_ENTRY_REQUEST]: approveEntryOptimistic,
  [FORCE_ADVANCE_ENTRY_SUCCESS]: setEntry,
  [FORCE_REMOVE_ENTRY_STAGE_SUCCESS]: removeEntryStage,
  [UPDATE_STORY_SUCCESS]: setStory,
  [REMOVE_ENTRY_MEDIA_REQUEST]: removeMediaOptimistic,
  [REMOVE_ENTRY_MEDIA_SUCCESS]: updateStory,
  [UPDATE_ENTRY_MEDIA_REQUEST]: updateMediaOptimistic,
  [UPDATE_ENTRY_MEDIA_SUCCESS]: updateStory,
  [REMOVE_ENTRY_MEDIA_THUMBNAIL_REQUEST]: removeVideoThumbnailOptimistic,
  [REMOVE_ENTRY_MEDIA_THUMBNAIL_SUCCESS]: updateStory,
  [UPDATE_ENTRY_MEDIA_THUMBNAIL_REQUEST]: updateVideoThumbnailOptimistic,
  [UPDATE_ENTRY_MEDIA_THUMBNAIL_SUCCESS]: updateStory,
  [CREATE_ENTRY_SUCCESS]: addEntryStory,
  [CLEAR_ENTRY_PROCESSING]: clearStoryProcessing,
  SET_ENTRY_STATUS_SUCCESS: SetEntryStatus,
};

export default createReducer(functionMap, storiesDefaultState);
